import {
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Slider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/system';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { getPlantSizeOptions } from '../../services/CustomerService';
import { useCustomerStore } from '../../store/CustomerJourneyStore';
import { usePageTitleStore } from '../../store/PageTitleStore';
import { findStateDistrict } from '../../services/CustomerService';
const ThickSlider = styled(Slider)(({ isMobile }) => ({
  color: 'primary.main',
  height: 10,
  '& .MuiSlider-thumb': {
    height: 24,
    width: 24,
    backgroundColor: '#fff',
    border: '2px solid currentColor',
    '&:focus, &:hover, &$active': {
      boxShadow: 'inherit',
    },
    display: 'none',
  },
  '& .MuiSlider-rail': {
    height: 40,
    backgroundColor: 'red',
    borderRadius: '12px',
  },
  '& .MuiSlider-track': {
    backgroundColor: '#A8D499',
    border: 'none',
    height: 40,
    borderRadius: '0px',
  },
  '&::before': {
    content: '"typical price range"',
    position: 'absolute',
    zIndex: '10',
    color: 'black',
    top: '20%',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '16px',
    fontFamily: '"Plus Jakarta Sans", sans-serif',
  },
  '& .MuiSlider-markLabel': {
    fontWeight: 'bold',
    fontSize: '22px',
    marginTop: 4,
  },
}));

const PlantSizeOptions = () => {
  const { updateTitle } = usePageTitleStore();
  const {
    useFormInputs,
    updateFormInputs,
    activeStep,
    setActiveStep,
    submitIsClicked,
    setSubmitIsClicked,
  } = useCustomerStore();
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [customSize, setCustomSize] = useState('None');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const createOptions = (plantOption) => {
    const optionsList = [];
    if (plantOption.fullEnergyNeedsPlantSize) {
      const plantData = plantOption.fullEnergyNeedsPlantSize.plantData;
      const option = {
        plantSize: `${plantData.numericPlantSize}kW`,
        title: '100% of energy needs',
        description: 'bill reduction',
        optionName: '',
        billReduction: plantData.billReduction,
        firstYearSavings: `₹ ${plantData.firstYearSavings} saved in first year`,
        energyConsumption: plantData.energyConsumption,
        energyProduction: plantData.energyProduction,
        minPrice: plantData.installationCostRange.split('-')[0],
        maxPrice: plantData.installationCostRange.split('-')[1],
      };
      optionsList.push(option);
    }
    if (plantOption.loadPlantSize) {
      const plantData = plantOption.loadPlantSize.plantData;
      const option = {
        plantSize: `${plantData.numericPlantSize}kW`,
        optionName: '(match existing load)',
        description: 'bill reduction',
        billReduction: plantData.billReduction,
        firstYearSavings: `₹ ${plantData.firstYearSavings} saved in first year`,
        energyConsumption: plantData.energyConsumption,
        energyProduction: plantData.energyProduction,
        minPrice: plantData.installationCostRange.split('-')[0],
        maxPrice: plantData.installationCostRange.split('-')[1],
        title: `${((plantData.energyProduction / plantData.energyConsumption) * 100).toFixed(0)}% of energy needs`,
      };
      optionsList.push(option);
    }
    //TODO: We will need this later, not now so commenting it out
    // if (plantOption.roofAreaPlantSize) {
    //   const plantData = plantOption.roofAreaPlantSize.plantData;
    //   const option = {
    //     plantSize: plantData.plantSize,
    //     description: 'reduce bill by',
    //     billReduction: plantData.billReduction,
    //     firstYearSavings: plantData.firstYearSavings,
    //     energyConsumption: plantData.energyConsumption,
    //     energyProduction: plantData.energyProduction,
    //     minPrice: plantData.installationCostRange.split('-')[0],
    //     maxPrice: plantData.installationCostRange.split('-')[1],
    //     title: `${((plantData.energyProduction / plantData.energyConsumption) * 100).toFixed(2)}% of energy needs`,
    //   };
    //   optionsList.push(option);
    // }
    if (plantOption.customSize) {
      const plantData = plantOption.customSize.plantData;
      const option = {
        plantSize: `${plantData.numericPlantSize}kW`,
        optionName: '(custom size)',
        description: 'bill reduction',
        billReduction: plantData.billReduction,
        firstYearSavings: `₹ ${plantData.firstYearSavings} saved in first year`,
        energyConsumption: plantData.energyConsumption,
        energyProduction: plantData.energyProduction,
        minPrice: plantData.installationCostRange.split('-')[0],
        maxPrice: plantData.installationCostRange.split('-')[1],
        title: `${((plantData.energyProduction / plantData.energyConsumption) * 100).toFixed(0)}% of energy needs`,
      };
      optionsList.push(option);
    }
    return optionsList;
  };

  const validationSchema = Yup.object().shape({
    selectedOption: Yup.string().required('Please select an option'),
  });

  useEffect(() => {
    const onMount = async () => {
      try {
        const region =
          useFormInputs.propertyDetails.metadata?.state ||
          (await findStateDistrict(useFormInputs.propertyDetails.pinCode)) ||
          'Delhi';
        const propertyType =
          useFormInputs.propertyDetails.propertyType || 'RESIDENTIAL';
        const billAmount = useFormInputs.propertyDetails.billAmount || 2000;
        const billMonth = useFormInputs.propertyDetails.billMonth || 'JANUARY';
        const sanctionedLoad =
          useFormInputs.propertyDetails.sanctionedLoad || 3;
        const shadowFreeArea =
          useFormInputs.propertyDetails.shadowFreeArea || 500;
        const loanPercentage = 0; //TODO: get from formik
        const data = await getPlantSizeOptions(
          region,
          propertyType,
          billAmount,
          billMonth,
          sanctionedLoad,
          shadowFreeArea,
          loanPercentage,
          customSize,
        );
        if (data.length === 0) throw new Error('No data found');
        setOptions(createOptions(data.plantOptions));
      } catch (error) {
        toast.error(error);
      }
    };
    onMount();
  }, [customSize]);

  useEffect(() => {
    updateTitle('Select plant size | VaySolar');
    setSubmitIsClicked(false);
  }, [setSubmitIsClicked, updateTitle]);

  const handleCardClick = (index) => {
    const newSelectedOption = selectedOption === index ? null : index;
    setSelectedOption(selectedOption === index ? null : index);
    formik.setFieldValue('selectedOption', newSelectedOption);
  };

  const handleCustomSizeChange = (event) => {
    setCustomSize(event.target.value);
    formik.setFieldValue('customSize', event.target.value);
  };

  const formik = useFormik({
    initialValues: {
      selectedOption: selectedOption || '',
      customSize: customSize || '',
    },
    validationSchema,
    onSubmit: (values) => {
      const selectedPlantSize =
        options[values.selectedOption]?.plantSize.split('kW')[0];
      updateFormInputs({
        ...useFormInputs,
        propertyDetails: {
          ...useFormInputs['propertyDetails'],
          plantSize: selectedPlantSize,
        },
        siteVisitData: {
          ...useFormInputs['siteVisitData'],
          recommendedLoad: selectedPlantSize,
        },
      });
      setTimeout(() => {
        setActiveStep(activeStep + 1);
      }, 500);
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (submitIsClicked) {
      formik.handleSubmit();
      setSubmitIsClicked(false);
    }
  }, [formik, setSubmitIsClicked, submitIsClicked]);

  return (
    <Container>
      <Stack
        alignItems={isMobile ? 'flex-start' : 'center'}
        pb={{ xs: '100px', sm: '20px' }}
      >
        <Typography
          sx={{
            fontSize: {
              xs: theme.typography.h4.fontSize,
              md: theme.typography.h3.fontSize,
            },
            fontWeight: 'bold',
            marginTop: { sm: '40px', xs: '10px' },
          }}
        >
          Select plant size
        </Typography>
        <Stack alignItems={isMobile ? 'flex-start' : 'center'} width={'100%'}>
          {options.map((option, index) => (
            <Card
              key={index}
              sx={{
                borderRadius: '16px',
                border: '1px solid lightgray',
                boxShadow: 'none',
                backgroundColor:
                  selectedOption === index
                    ? theme.palette.secondary.lighter
                    : theme.palette.background.paper,
                cursor: 'pointer',
                width: '100%',
                marginTop: 1,
              }}
              onClick={() => handleCardClick(index)}
            >
              <CardContent>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 'bold', paddingBottom: '4px' }}
                >
                  <Typography
                    variant='h6'
                    fontWeight={'bold'}
                    component='span'
                    sx={{ color: 'primary.main' }}
                  >
                    {option.plantSize}
                  </Typography>
                  , {option.title}
                </Typography>

                {selectedOption === index && (
                  <>
                    <Grid container mt={5}>
                      <Grid item xs={6.5}>
                        <Typography
                          variant='subtitle1'
                          fontWeight={'bold'}
                          sx={{ color: 'primary.main' }}
                        >
                          Annual energy needs
                        </Typography>
                      </Grid>
                      <Grid item xs={5.5} style={{ textAlign: 'right' }}>
                        <Typography
                          variant='subtitle1'
                          sx={{ fontWeight: 'bold' }}
                        >
                          {option.energyConsumption.toLocaleString()} units
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container>
                      <Grid item xs={7.5}>
                        <Typography
                          variant='subtitle1'
                          fontWeight='bold'
                          sx={{ color: 'primary.main' }}
                        >
                          {option.plantSize} plant production
                        </Typography>
                      </Grid>
                      <Grid item xs={4.5} style={{ textAlign: 'right' }}>
                        <Typography
                          variant='subtitle1'
                          sx={{ fontWeight: 'bold' }}
                        >
                          {option.energyProduction.toLocaleString()} units
                        </Typography>
                      </Grid>
                    </Grid>

                    <ThickSlider
                      value={[20, 80]}
                      min={0}
                      max={100}
                      sx={{
                        mt: 8,
                        width: { sm: '60%', xs: '100%' },
                        mx: 'auto',
                      }}
                      marks={[
                        {
                          value: 20,
                          label: `₹${option.minPrice}`,
                        },
                        {
                          value: 80,
                          label: `₹${option.maxPrice}`,
                        },
                      ]}
                      isMobile={isMobile}
                    />
                  </>
                )}
              </CardContent>
            </Card>
          ))}
          <Typography
            sx={{
              mt: 4,
              fontWeight: 'bold',
              alignSelf: isMobile ? 'flex-end' : 'center',
            }}
          >
            Custom Plant Size (kW)
          </Typography>
          <FormControl
            margin='dense'
            sx={{
              mt: 1,
              maxWidth: '250px',
              alignSelf: isMobile ? 'flex-end' : 'center',
            }}
            error={
              formik.touched.customSize && Boolean(formik.errors.customSize)
            }
          >
            <Select value={customSize} onChange={handleCustomSizeChange}>
              <MenuItem value='None'>
                <em>None</em>
              </MenuItem>
              {[...Array(10).keys()].map((value) => (
                <MenuItem key={value + 1} value={value + 1}>
                  {value + 1} kW
                </MenuItem>
              ))}
            </Select>
            {formik.touched.customSize && formik.errors.customSize && (
              <Typography sx={{ color: theme.palette.error.main, mt: 1 }}>
                {formik.errors.customSize}
              </Typography>
            )}
          </FormControl>

          {formik.touched.selectedOption && formik.errors.selectedOption ? (
            <Typography sx={{ color: theme.palette.error.main, mt: 1 }}>
              {formik.errors.selectedOption}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Container>
  );
};

export default PlantSizeOptions;
